import { ExecutionStatus, TransportOrder } from '../transportAssignment.types';
import { isValidDate } from '../../sharedComponents/dateHelper';
import { useMemo, useState } from 'react';
import { usePostStatusTransportAssignmentMutation } from '../../api/transportOrderApi';
import { runInBackground } from '../../../../configuration/setup/backgroundActions';
import { showSuccessNotificationAfterStatusUpdate } from '../../notifications/SuccessNotification';
import { handleQueryError } from '../../notifications/ErrorNotification';
import { validateAcrossAssignments } from './transportOrderBulkUpdateFormValidation';
import { getEmptyValidationResult, getExecutionStatusDateErrorMessage } from '../shared/sharedFormValidations';
import { ValidationResult } from '../../sharedComponents/validationResult.type';
import { getUniqueValueOrUndefined } from '../../sharedComponents/dataHelper';

export const validateExecutionStatusDate = (
    date: Date | string | undefined,
    orderIssuedAt: Date,
    loadingDate?: Date | string,
): ValidationResult => {
    if (date === undefined) {
        return getEmptyValidationResult();
    }

    return {
        error: getExecutionStatusDateErrorMessage(date, orderIssuedAt, loadingDate),
        warning: undefined,
    };
};

const getLatestExecutionStatusDate = (transportOrder: TransportOrder, targetStatus: ExecutionStatus) => {
    const matchingEvents = transportOrder.executionEvents.filter((event) => event.eventStatus === targetStatus);

    return matchingEvents.reduce<Date | undefined>((latestDate, event) => {
        if (latestDate === undefined || latestDate.getTime() < event.eventAt.getTime()) {
            return event.eventAt;
        }

        return latestDate;
    }, undefined);
};

export const useBulkUpdateExecutionStatusForm = (assignments: TransportOrder[], status: ExecutionStatus) => {
    const [changeTransportAssignmentStatus, statusRequest] = usePostStatusTransportAssignmentMutation();
    const [executionDate, setExecutionDate] = useState<Date | undefined>(
        getUniqueValueOrUndefined(assignments.map((assignment) => getLatestExecutionStatusDate(assignment, status))),
    );

    const loadingDate = useMemo(
        () =>
            getUniqueValueOrUndefined(
                assignments.map((assignment) => getLatestExecutionStatusDate(assignment, ExecutionStatus.LOADED)),
            ),
        [assignments],
    );

    const handleExecutionStatusUpdate = () => {
        if (!isValidDate(executionDate)) {
            return;
        }

        runInBackground(async () => {
            try {
                await changeTransportAssignmentStatus({
                    executionStatusChanged: {
                        transportOrderIds: assignments.map((assignment) => assignment.transportOrderId),
                        vehicleIds: assignments.map((assignment) => assignment.vehicle.id),
                        status,
                        statusAt: executionDate,
                    },
                }).unwrap();
                showSuccessNotificationAfterStatusUpdate(status);
            } catch (error: unknown) {
                handleQueryError(error);
            }
        });
    };

    const validation =
        status === ExecutionStatus.LOADED
            ? validateAcrossAssignments(assignments, (assignment) =>
                  validateExecutionStatusDate(executionDate, assignment.orderIssuedAt),
              )
            : validateAcrossAssignments(assignments, (assignment) =>
                  validateExecutionStatusDate(
                      executionDate,
                      assignment.orderIssuedAt,
                      isValidDate(loadingDate)
                          ? loadingDate
                          : getLatestExecutionStatusDate(assignment, ExecutionStatus.LOADED),
                  ),
              );

    return {
        executionDate,
        validation,
        setExecutionDate,
        isLoading: statusRequest.isLoading,
        handleExecutionStatusUpdate,
    };
};
